import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Spinner,
  apiFetch,
  type PortalLoginHandoffRequest,
  type PortalLoginHandoffResponse,
  type PortalLoginResponse,
} from "@dearbill/shared";
import { usePortalSession } from "./hooks/usePortalSession";
import { PortalShell } from "./PortalShell";
import { PortalLogin } from "./PortalLogin";
import { EMPTY_LOGIN_FIELDS, type PortalLoginFields } from "./loginFields";
import { PortalOtp } from "./PortalOtp";
import { MemberDashboard } from "./MemberDashboard";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Login-form notice when the enrollment-flow handoff is rejected. */
const HANDOFF_EXPIRED_MESSAGE = "Your sign-in link has expired. Please sign in below.";

/** The `#handoff=<token>` fragment the enrollment flow's Done CTA lands with, or null. */
function readHandoff(): string | null {
  return new URLSearchParams(window.location.hash.slice(1)).get("handoff");
}

/**
 * The OTP-gated member login, the layout route of every signed-in screen. The
 * steps are path-agnostic, so after signing in the member stays on the path
 * they opened.
 *
 *   - No session and no challenge: the identity-challenge login (phone and MBI,
 *     or phone, last name, DOB and ZIP, per the sign-in-method toggle).
 *   - A challenge but no session: OTP entry, where the user picks SMS or a
 *     voice call and sends the code.
 *   - A session: `MemberDashboard`, whose outlet renders the screen for the
 *     current path, with its own layout and sign-out control instead of
 *     `PortalShell`.
 *   - `#handoff=<token>` in the URL, from the enrollment flow's Done CTA: a
 *     "Signing you in…" state while the single-use token is exchanged at
 *     `POST /api/portal/login/handoff`, the portal's one session mint without
 *     an OTP. Success opens the dashboard; failure shows the login form with an
 *     expired-link notice. The fragment is stripped from the address bar on
 *     arrival, and it wins over any session already in localStorage.
 *
 * On a document sign link (`/forms/:enrollmentId/sign/:documentSlug`) the same
 * gate applies, and the member stays on the link through login, so a
 * signature request's link opens its sign page once the member is signed in.
 *
 * Login doesn't mint a session: it returns a short-lived `challenge` and sends
 * no code. Only passing the OTP yields a session. The identity fields live
 * here, so "Back / edit info" returns to a pre-filled form. The challenge is
 * React state only, so a hard reload returns to the login form.
 */
export function Portal(): ReactNode {
  const { session, setSession, clear } = usePortalSession();
  const { enrollmentId, documentSlug } = useParams();
  const documentSign =
    enrollmentId !== undefined && documentSlug !== undefined
      ? { enrollmentId, slug: documentSlug }
      : undefined;
  const [searchParams] = useSearchParams();

  // `?role=caregiver`, which the caregiver-invitation page's Sign in link
  // carries, preselects the Caregiver role; everything else starts blank.
  const [fields, setFields] = useState<PortalLoginFields>(() => ({
    ...EMPTY_LOGIN_FIELDS,
    role: searchParams.get("role") === "caregiver" ? "caregiver" : "member",
  }));
  // The whole step-1 response (the challenge and `bypassSmsOtp`), so the OTP
  // step's bypass banner reads the flag with the token it belongs to.
  // `PortalLogin` forwards only challenge-bearing responses and handles the
  // `mbiRequired` and `identityAmbiguous` 200s itself; the render guard below
  // still checks `challenge`, because the type is nullable.
  const [login, setLogin] = useState<PortalLoginResponse | null>(null);
  const [restartMessage, setRestartMessage] = useState<string | null>(null);

  // Non-null while the handoff exchange is in flight. It is seeded from the URL
  // at mount, so the login form never flashes before the effect runs. `claimed`
  // makes the single-use exchange fire exactly once: StrictMode runs effects
  // twice, and a second POST would get a 401 against its own claim.
  const [handoff, setHandoff] = useState(readHandoff);
  const claimed = useRef(false);

  useEffect(() => {
    if (handoff === null || claimed.current) return;
    claimed.current = true;
    // Strip the token from the address bar first, keeping it out of history and
    // bookmarks; the router's own history state is preserved.
    const routerState: unknown = window.history.state;
    window.history.replaceState(routerState, "", window.location.pathname + window.location.search);
    apiFetch<PortalLoginHandoffResponse>(
      "/api/portal/login/handoff",
      { method: "POST", body: { handoff } satisfies PortalLoginHandoffRequest },
      { baseUrl: API_BASE },
    )
      // Replaces any existing session, because a stale one for another patient
      // in this browser must not win over the identity the flow just proved.
      .then(({ session: minted }) => setSession(minted))
      .catch(() => setRestartMessage(HANDOFF_EXPIRED_MESSAGE))
      .finally(() => setHandoff(null));
  }, [handoff, setSession]);

  const onAuthenticated = useCallback(
    (token: string): void => {
      setLogin(null); // The challenge is consumed; a later sign-out must land on the login form.
      setSession(token);
    },
    [setSession],
  );

  const onRestart = useCallback((message?: string): void => {
    setLogin(null);
    setRestartMessage(message ?? null);
  }, []);

  // Last 4 digits of the entered phone, for the OTP channel-prompt copy.
  const phoneHint = fields.phone.replace(/\D/g, "").slice(-4) || undefined;

  // Before the session branch on purpose: the handoff wins over a stored session.
  if (handoff !== null) {
    return (
      <PortalShell session={null}>
        <div className="text-slate-teal flex items-center gap-3">
          <Spinner /> <span>Signing you in…</span>
        </div>
      </PortalShell>
    );
  }

  if (session) {
    return <MemberDashboard session={session} onSignOut={clear} documentSign={documentSign} />;
  }

  return (
    <PortalShell session={session}>
      {login?.challenge ? (
        <PortalOtp
          challenge={login.challenge}
          bypassSmsOtp={login.bypassSmsOtp}
          phoneHint={phoneHint}
          onAuthenticated={onAuthenticated}
          onRestart={onRestart}
        />
      ) : (
        <>
          {restartMessage && <Alert tone="warning" title={restartMessage} className="mb-4" />}
          <PortalLogin
            fields={fields}
            onFieldsChange={(next) => {
              setFields(next);
              if (restartMessage) setRestartMessage(null);
            }}
            onChallenge={setLogin}
          />
        </>
      )}
    </PortalShell>
  );
}
